/**
 * phase-a2-assistant-panel-submission-qa / phase-a3-assistant-global-scope —
 * the AI Assistant slide-over (design §7). StatusDrawer pattern: fixed right
 * column over a backdrop, Escape/backdrop close, sticky header + scroll
 * body — plus a footer input.
 *
 * Scopes: this-submission (on a submission page), All submissions
 * (portfolio Q&A — the Dashboard's default), and General (explanatory).
 * Scope is fixed per conversation; switching starts a fresh one. Model
 * override UI is A4 — every answer names the model that produced it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  v2,
  type AssistantConversation,
  type AssistantCitation,
  type AssistantCatalogModel,
  type AssistantStatus,
} from '../../api/v2Client';
import { AssistantActions } from './AssistantActions';
import {
  assistantStatusMessage,
  describeAssistantError,
  describeAssistantStreamError,
  isConfigurationError,
  type AssistantErrorDescription,
  type AssistantErrorKind,
} from '../../lib/assistantErrors';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  modelDisplay?: string;
  /** phase-a4 — 'Recommended' | 'Your selection' footer tag. */
  modelSelectionLabel?: string;
  /** phase-a4 — honest routing note (cap / risky downgrade). */
  routingNote?: string;
  citations?: AssistantCitation[];
  error?: boolean;
  /** Test #9 — which kind of failure this error bubble is (drives its test id). */
  errorKind?: AssistantErrorKind;
}

/**
 * phase-a4 — the model picker's choices (Auto = server routes per question).
 * phase-assistant-model-catalog — a choice is now 'auto' or a catalog model id
 * (e.g. 'claude-fable-5'); the option list is fetched from the server catalog,
 * not hardcoded here.
 */
type ModelChoice = string;

export type AssistantScope = 'submission' | 'global' | 'explanatory';

interface AssistantPanelProps {
  open: boolean;
  docked?: boolean;
  width?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
  onClose: () => void;
  /** Present when opened from a submission page (route context). */
  submissionId?: string | null;
  /** Display label for the scope pill, e.g. "Norvantis · Seq 0000". */
  submissionLabel?: string | null;
  /** Route-derived starting scope (Dashboard → 'global'). */
  defaultScope: AssistantScope;
}

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.35)',
  zIndex: 900,
};

const panelStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 420,
  maxWidth: '95vw',
  background: 'var(--surface)',
  borderLeft: '1px solid var(--border)',
  boxShadow: '-8px 0 24px rgba(15, 23, 42, 0.12)',
  zIndex: 901,
  display: 'flex',
  flexDirection: 'column',
  animation: 'assistantSlideIn 250ms ease-out',
};

const headerStyle: CSSProperties = {
  padding: '14px 16px 10px 16px',
  borderBottom: '1px solid var(--border)',
  flexShrink: 0,
};

const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  padding: '3px 10px',
  borderRadius: 999,
  background: 'var(--accent-soft, rgba(59,130,246,0.1))',
  color: 'var(--accent, #2563eb)',
  border: '1px solid var(--accent, #2563eb)',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const inputRowStyle: CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: 12,
  display: 'flex',
  gap: 8,
  flexShrink: 0,
};

const bubbleBase: CSSProperties = {
  borderRadius: 10,
  padding: '8px 12px',
  fontSize: 13,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

function CitationChips({
  citations,
  onNavigate,
}: {
  citations: AssistantCitation[];
  onNavigate: (link: string) => void;
}): ReactElement | null {
  if (citations.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {citations.map((c) => (
        <button
          key={c.sourceId}
          type="button"
          data-testid={`assistant-citation-${c.sourceId}`}
          onClick={() => onNavigate(c.deepLink)}
          title={`Verify: ${c.label}`}
          style={{
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 999,
            border: '1px solid var(--border)',
            background: 'var(--surface-2, #f8fafc)',
            cursor: 'pointer',
            color: 'var(--text)',
          }}
        >
          ↗ {c.label}
        </button>
      ))}
    </div>
  );
}

export function AssistantPanel({
  open,
  docked = false,
  width = 420,
  maxWidth = 640,
  onWidthChange,
  onClose,
  submissionId,
  submissionLabel,
  defaultScope,
}: AssistantPanelProps): ReactElement | null {
  const navigate = useNavigate();
  const resizeStart = useRef<{ x: number; width: number } | null>(null);
  const resizeTo = (next: number): void => onWidthChange?.(Math.max(320, Math.min(maxWidth, next)));
  const [scope, setScope] = useState<AssistantScope>(defaultScope);
  // Follow route context whenever it changes (a panel opened on the
  // Dashboard defaults to the portfolio; on a submission, to it).
  useEffect(() => {
    setScope(defaultScope);
  }, [defaultScope]);
  const [conversation, setConversation] = useState<AssistantConversation | null>(null);
  const [recent, setRecent] = useState<AssistantConversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [footer, setFooter] = useState<string | null>(null);
  const [modelChoice, setModelChoice] = useState<ModelChoice>('auto');
  // phase-assistant-model-catalog — enabled models from the server catalog.
  const [catalog, setCatalog] = useState<AssistantCatalogModel[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Test #9 — configuration status (null until read) and the rate-limit
  // countdown. A panel that is not usable says so before the user types.
  const [configStatus, setConfigStatus] = useState<AssistantStatus | null>(null);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const abortRef = useRef<globalThis.AbortController | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Escape closes (dialog convention).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Load recent conversations on open; focus the input.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadError(null);
    v2.assistant
      .listConversations()
      .then((rows) => {
        if (!cancelled) setRecent(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load conversations.');
      });
    // Test #9 — is the assistant usable at all? Not configured → banner +
    // disabled input, never a blank pane. An unreadable status does not
    // block: the send path maps its own errors.
    setConfigStatus(null);
    v2.assistant
      .getStatus()
      .then((status) => {
        if (!cancelled) setConfigStatus(status);
      })
      .catch(() => {
        if (!cancelled) setConfigStatus({ configured: true });
      });
    // Load the model catalog for the picker (Auto + enabled models).
    v2.assistant
      .getModelCatalog()
      .then((data) => {
        if (!cancelled) setCatalog(data.models);
      })
      .catch(() => {
        /* picker degrades to Auto-only; routing still answers */
      });
    const t = window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 120);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [open]);

  // Test #9 — one-second countdown while a rate limit is in force.
  useEffect(() => {
    if (rateLimitUntil === null) return;
    const tick = (): void => {
      const now = Date.now();
      setNowTick(now);
      if (now >= rateLimitUntil) setRateLimitUntil(null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [rateLimitUntil]);
  const rateLimitRemaining =
    rateLimitUntil === null ? 0 : Math.max(0, Math.ceil((rateLimitUntil - nowTick) / 1000));
  const configMessage = configStatus === null ? null : assistantStatusMessage(configStatus);
  const inputBlocked = configMessage !== null || rateLimitRemaining > 0;

  // Auto-scroll on new content (scrollTo is absent in jsdom — guard).
  useEffect(() => {
    const el = bodyRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [messages]);

  const resumeConversation = useCallback(async (conv: AssistantConversation) => {
    try {
      const data = await v2.assistant.getMessages(conv.Id);
      setConversation(data.conversation);
      setModelChoice(data.conversation.ModelOverride ?? 'auto');
      setMessages(
        data.messages.map((m) => ({
          id: m.Id,
          role: m.Role,
          content: m.Content,
          ...(m.Model ? { modelDisplay: m.Model } : {}),
        })),
      );
    } catch {
      setLoadError('Could not load that conversation.');
    }
  }, []);

  const startNew = useCallback(() => {
    setConversation(null);
    setMessages([]);
    setFooter(null);
    setModelChoice('auto'); // per-conversation setting; new ones start on Auto
  }, []);

  // phase-a4 — pick a model; persists immediately on an open conversation
  // (a brand-new one gets it right after creation in send()).
  const chooseModel = useCallback(
    (next: ModelChoice) => {
      setModelChoice(next);
      if (conversation) {
        v2.assistant
          .setModelOverride(conversation.Id, next)
          .then((updated) => setConversation(updated))
          .catch(() => {
            /* keep the local choice; next send re-persists */
          });
      }
    },
    [conversation],
  );

  const switchScope = useCallback(
    (next: AssistantScope) => {
      if (next === scope) return;
      setScope(next);
      startNew(); // scope is fixed per conversation
    },
    [scope, startNew],
  );

  // Test #9 — one place turns a failure into the bubble the user sees: the
  // server's own message for configuration / rate-limit / unavailable codes,
  // the generic text only for truly unknown failures. Configuration problems
  // also raise the banner; a rate limit starts the countdown.
  const showFailure = useCallback((d: AssistantErrorDescription) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      const bubble: ChatMessage = {
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: d.message,
        error: true,
        errorKind: d.kind,
      };
      // Reuse the empty streaming placeholder rather than stacking a
      // second bubble under it.
      if (last && last.role === 'assistant' && last.content === '') {
        return prev.map((m) => (m.id === last.id ? { ...bubble, id: last.id } : m));
      }
      return [...prev, bubble];
    });
    if (isConfigurationError(d) && d.code !== null) {
      setConfigStatus({
        configured: false,
        reason: d.code === 'AssistantKeyInvalid' ? 'AssistantKeyInvalid' : 'AssistantNotConfigured',
      });
    }
    if (d.kind === 'rate-limited' && d.retryAfterSeconds !== null) {
      // Stamp the clock with the deadline so the FIRST render already shows
      // the server's number (a stale tick briefly read "31s" for a 30 s wait).
      const now = Date.now();
      setNowTick(now);
      setRateLimitUntil(now + d.retryAfterSeconds * 1000);
    }
  }, []);

  const send = useCallback(async () => {
    const content = draft.trim();
    if (!content || streaming || inputBlocked) return;
    setDraft('');
    setStreaming(true);
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content }]);

    try {
      let conv = conversation;
      if (!conv) {
        conv = await v2.assistant.createConversation(
          scope === 'submission' && submissionId
            ? { scope: 'submission', submissionId }
            : { scope: scope === 'submission' ? 'explanatory' : scope },
        );
        if (modelChoice !== 'auto') {
          try {
            conv = await v2.assistant.setModelOverride(conv.Id, modelChoice);
          } catch {
            /* best-effort; Auto routing still answers */
          }
        }
        setConversation(conv);
      }
      const assistantId = `a-${Date.now()}`;
      setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);
      const abort = new globalThis.AbortController();
      abortRef.current = abort;
      for await (const evt of v2.assistant.ask(conv.Id, content, abort.signal)) {
        if (evt.type === 'token') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: m.content + evt.delta } : m,
            ),
          );
        } else if (evt.type === 'done') {
          setFooter(evt.footer);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    modelDisplay: evt.modelDisplay,
                    modelSelectionLabel: evt.modelSelectionLabel,
                    ...(evt.routingNote ? { routingNote: evt.routingNote } : {}),
                    citations: evt.citations,
                  }
                : m,
            ),
          );
        } else {
          // Test #9 — an `error` frame after the stream started carries the
          // same {error, message} shape as the HTTP bodies.
          showFailure(describeAssistantStreamError(evt));
        }
      }
    } catch (err) {
      showFailure(describeAssistantError(err));
    } finally {
      abortRef.current = null;
      setStreaming(false);
    }
  }, [draft, streaming, inputBlocked, conversation, submissionId, scope, modelChoice, showFailure]);

  useEffect(() => () => abortRef.current?.abort(), []);

  if (!open) return null;

  const scopeLabel =
    scope === 'submission' && submissionId
      ? `Asking about: ${submissionLabel ?? 'this submission'}`
      : scope === 'global'
        ? 'Asking about: All submissions'
        : 'General questions';

  return (
    <>
      {!docked && <div
        style={backdropStyle}
        data-testid="assistant-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />}
      <aside
        style={docked ? { ...panelStyle, width, top: 72, boxShadow: 'none', animation: 'none', zIndex: 85 } : panelStyle}
        role={docked ? 'complementary' : 'dialog'}
        data-layout={docked ? 'docked' : 'overlay'}
        aria-label="AI Assistant"
        data-testid="assistant-panel"
      >
        {docked && onWidthChange && <div
          role="separator"
          aria-label="Resize AI assistant"
          aria-orientation="vertical"
          aria-valuemin={320}
          aria-valuemax={maxWidth}
          aria-valuenow={width}
          aria-valuetext={`${width} pixels wide`}
          tabIndex={0}
          className="assistant-resize-handle"
          title="Drag to resize. Arrow keys adjust width; double-click to reset."
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            resizeStart.current = { x: event.clientX, width };
            event.currentTarget.setPointerCapture(event.pointerId);
            event.currentTarget.focus({ preventScroll: true });
          }}
          onPointerMove={(event) => {
            const start = resizeStart.current;
            if (start) resizeTo(start.width + start.x - event.clientX);
          }}
          onPointerUp={(event) => {
            resizeStart.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => { resizeStart.current = null; }}
          onLostPointerCapture={() => { resizeStart.current = null; }}
          onDoubleClick={() => resizeTo(420)}
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            resizeTo(event.key === 'Home' ? 320 : event.key === 'End' ? maxWidth : width + (event.key === 'ArrowLeft' ? 20 : -20));
          }}
        />}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 14 }}>✨ AI Assistant</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="assistant-new-conversation"
                onClick={startNew}
                style={{ fontSize: 12, background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: 'var(--text)' }}
              >
                New
              </button>
              <button
                type="button"
                aria-label="Close assistant"
                data-testid="assistant-close"
                onClick={onClose}
                style={{ fontSize: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                ✕
              </button>
            </div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={pillStyle} data-testid="assistant-scope-pill">
              {scopeLabel}
            </span>
            {(
              [
                ...(submissionId ? ([['submission', 'This submission']] as const) : []),
                ['global', 'All submissions'],
                ['explanatory', 'General'],
              ] as ReadonlyArray<readonly [AssistantScope, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-testid={`assistant-scope-${value}`}
                aria-pressed={scope === value}
                onClick={() => switchScope(value)}
                style={{
                  fontSize: 11,
                  padding: '3px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--border)',
                  background: scope === value ? 'var(--accent, #2563eb)' : 'transparent',
                  color: scope === value ? '#fff' : 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
            <select
              data-testid="assistant-model-select"
              aria-label="Model"
              value={modelChoice}
              onChange={(e) => chooseModel(e.target.value as ModelChoice)}
              style={{
                marginLeft: 'auto',
                fontSize: 11,
                padding: '3px 6px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                cursor: 'pointer',
              }}
            >
              <option value="auto">Auto (Recommended)</option>
              {catalog.map((m) => (
                <option key={m.modelId} value={m.modelId}>
                  {m.displayName}
                  {m.preferredForTier && m.tier === 'advanced' ? ' — advanced' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {!streaming && messages.at(-1)?.role === 'assistant' ? messages.at(-1)?.content : ''}
        </div>
        <div style={bodyStyle} ref={bodyRef} data-testid="assistant-messages">
          {scope === 'submission' && submissionId && (
            <AssistantActions submissionId={submissionId} />
          )}
          {configMessage !== null && (
            <div
              role="alert"
              data-testid="assistant-config-banner"
              style={{ ...bubbleBase, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}
            >
              {configMessage}
            </div>
          )}
          {loadError && (
            <div style={{ ...bubbleBase, background: '#fef2f2', color: '#991b1b' }}>{loadError}</div>
          )}
          {messages.length === 0 && !loadError && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }} data-testid="assistant-empty">
              <p style={{ marginTop: 0 }}>
                Ask about{' '}
                {scope === 'submission' && submissionId
                  ? 'this submission'
                  : scope === 'global'
                    ? 'your whole portfolio'
                    : 'the system'}{' '}
                — status, blockers, documents, validation failures, or what a rule or eCTD
                section means. Answers come from your real data, with links to verify every
                claim.
              </p>
              {recent.length > 0 && (
                <>
                  <p style={{ marginBottom: 4, fontWeight: 600 }}>Recent conversations</p>
                  {recent.slice(0, 5).map((c) => (
                    <button
                      key={c.Id}
                      type="button"
                      data-testid={`assistant-resume-${c.Id}`}
                      onClick={() => void resumeConversation(c)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '6px 8px', marginBottom: 4, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <span style={{ color: 'var(--text-muted)' }}>
                        {/* Fix 1 — submission entries name their submission; a
                            removed submission degrades, never crashes. */}
                        [
                        {c.Scope === 'global'
                          ? 'All'
                          : c.Scope === 'submission'
                            ? c.SubmissionDrugName
                              ? `${c.SubmissionDrugName} ${c.SubmissionSequenceNumber ?? ''}`.trim()
                              : 'Sub — removed'
                            : 'Gen'}
                        ]
                      </span>{' '}
                      {c.Title ?? '(untitled conversation)'}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={`assistant-msg-${m.role}`}
              style={{
                ...bubbleBase,
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '92%',
                background: m.error
                  ? '#fef2f2'
                  : m.role === 'user'
                    ? 'var(--accent, #2563eb)'
                    : 'var(--surface-2, #f1f5f9)',
                color: m.error ? '#991b1b' : m.role === 'user' ? '#fff' : 'var(--text)',
              }}
            >
              {m.error && m.errorKind !== undefined && m.errorKind !== 'unknown' && m.errorKind !== 'unavailable' && m.errorKind !== 'disabled' ? (
                <div
                  data-testid={m.errorKind === 'rate-limited' ? 'assistant-rate-limit' : 'assistant-config-error'}
                >
                  {m.content}
                  {m.errorKind === 'rate-limited' && rateLimitRemaining > 0 && (
                    <div data-testid="assistant-rate-limit-countdown" style={{ marginTop: 4, fontWeight: 600 }}>
                      Try again in {rateLimitRemaining}s
                    </div>
                  )}
                </div>
              ) : (
                m.content || (streaming ? '…' : '')
              )}
              {m.citations && (
                <CitationChips
                  citations={m.citations}
                  onNavigate={(link) => navigate(link)}
                />
              )}
              {m.modelDisplay && !m.error && (
                <div
                  data-testid="assistant-model-label"
                  style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}
                >
                  Answered with {m.modelDisplay}
                  {m.modelSelectionLabel ? ` · ${m.modelSelectionLabel}` : ''}
                </div>
              )}
              {m.routingNote && !m.error && (
                <div
                  data-testid="assistant-routing-note"
                  style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}
                >
                  {m.routingNote}
                </div>
              )}
            </div>
          ))}
          {footer && (
            <div
              data-testid="assistant-footer"
              style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginTop: 4 }}
            >
              {footer}
            </div>
          )}
        </div>

        <div style={inputRowStyle}>
          <input
            ref={inputRef}
            data-testid="assistant-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void send();
            }}
            placeholder={
              configMessage !== null
                ? configStatus?.reason === 'AssistantKeyInvalid'
                  ? 'The assistant is unavailable — see the message above'
                  : 'The assistant is not configured'
                : rateLimitRemaining > 0
                  ? `Try again in ${rateLimitRemaining}s`
                  : scope === 'submission' && submissionId
                    ? 'Ask about this submission…'
                    : scope === 'global'
                      ? 'Ask across all submissions…'
                      : 'Ask about rules, sections, concepts…'
            }
            aria-label="Message the assistant"
            disabled={streaming || inputBlocked}
            style={{ flex: 1, fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
          />
          <button
            type="button"
            data-testid="assistant-send"
            onClick={() => void send()}
            disabled={streaming || inputBlocked || draft.trim().length === 0}
            style={{ fontSize: 13, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--accent, #2563eb)', color: '#fff', cursor: streaming ? 'wait' : 'pointer', opacity: streaming || inputBlocked || draft.trim().length === 0 ? 0.6 : 1 }}
          >
            {streaming ? '…' : rateLimitRemaining > 0 ? `Wait ${rateLimitRemaining}s` : 'Send'}
          </button>
        </div>
        <style>{`@keyframes assistantSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      </aside>
    </>
  );
}
